import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Eye, EyeOff, Lock, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminLoginPage() {
  const { login, admin } = useAdminAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (admin) return <Navigate to="/admin/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(form.username, form.password);
      if (!result.success) {
        setError(result.error || 'Invalid username or password');
        toast.error('Login failed. Check credentials.');
      } else {
        toast.success('Welcome back, Admin!');
      }
    } catch (err) {
      setError('Connection error. Please check if the server is running.');
      toast.error('Login failed.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f8ff] via-white to-[#e8f4fb] flex items-center justify-center p-4">
      {/* Background decorative blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#47ace2]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#1f7db3]/8 rounded-full blur-3xl" />
        <div className="absolute top-3/4 left-1/3 w-48 h-48 bg-[#47ace2]/6 rounded-full blur-2xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Card */}
        <div className="bg-white border border-gray-200 rounded-3xl p-10 shadow-2xl shadow-slate-200/80">
          {/* Logo & Brand */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 mb-4">
              <img src="/logo.png" alt="Modern Arch Logo" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 font-outfit">Modern Arch</h1>
            <p className="text-slate-400 font-manrope text-sm mt-1">Admin Control Panel</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-slate-600 font-manrope mb-2">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  id="admin-username"
                  value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  placeholder="Enter username"
                  required
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-slate-800 placeholder-slate-400 font-manrope text-sm focus:outline-none focus:border-[#47ace2] focus:ring-2 focus:ring-[#47ace2]/15 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-600 font-manrope mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="admin-password"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Enter password"
                  required
                  className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-slate-800 placeholder-slate-400 font-manrope text-sm focus:outline-none focus:border-[#47ace2] focus:ring-2 focus:ring-[#47ace2]/15 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-red-600 text-sm font-manrope">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              id="admin-login-btn"
              className="w-full py-3 bg-[#47ace2] hover:bg-[#1f7db3] text-white font-semibold font-manrope rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2 shadow-md shadow-[#47ace2]/30"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Signing in...
                </>
              ) : 'Sign In to Admin Panel'}
            </button>
          </form>

        </div>

        <p className="text-center text-slate-400 text-xs font-manrope mt-6">
          © 2026 Modern Arch Developers. All rights reserved.
        </p>
      </div>
    </div>
  );
}
